/**
 * Матрица истории культур шага «История» — данные (AK-772 PR-2).
 *
 * Читает `GET /farms/{id}/crop-history` (сетка «поле × 7 лет» + итоги + право
 * на перезапись) и `…/crop-history/log` (журнал клетки). ПИШЕТ штатная ручка
 * года — `PUT /fields/{id}/rotation/{year}` (`components/fields/card/history`):
 * писатель истории в продукте один, и матрица не заводит второго.
 */
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface HistoryCell {
  year: number;
  kind: 'crop' | 'fallow';
  crop_id: string | null;
  label: string;
  yield_c_ha: number | null;
  /** >1 — год спорный: правится в карточке поля, не одной клеткой. */
  sowings_count: number;
  /** Клетку правил человек — импорт «Истории поля» её не перезапишет. */
  manual: boolean;
  edited_at: string | null;
  edited_by: string | null;
  from_import: boolean;
}

export interface HistoryFieldRow {
  field_id: string;
  name: string;
  number: string | null;
  brigade_name: string | null;
  area_ha: number | null;
  empty_years: number;
  cells: HistoryCell[];
}

export interface CropHistoryGrid {
  farm_id: string;
  farm_name: string;
  years: number[];
  fields_total: number;
  cells_total: number;
  cells_filled: number;
  fields_without_history: number;
  /** Вправе ли ЭТОТ человек переписывать заполненные годы (замочек в UI). */
  can_overwrite: boolean;
  total: number;
  limit: number;
  offset: number;
  items: HistoryFieldRow[];
}

export interface HistoryLogEntry {
  changed_at: string;
  change_kind: 'fill' | 'overwrite';
  old_label: string | null;
  new_label: string | null;
  old_yield_c_ha: number | null;
  new_yield_c_ha: number | null;
  changed_by: string | null;
}

export interface CropHistoryFilters {
  onlyGaps: boolean;
  search: string;
  offset: number;
}

export const cropHistoryKeys = {
  grid: (farmId: string | null, f: CropHistoryFilters) =>
    ['crop-history-grid', farmId ?? 'none', f.onlyGaps, f.search, f.offset] as const,
  /** Префикс для инвалидации всей сетки хозяйства после правки клетки. */
  farm: (farmId: string | null) => ['crop-history-grid', farmId ?? 'none'] as const,
  log: (farmId: string | null, fieldId: string, year: number) =>
    ['crop-history-log', farmId ?? 'none', fieldId, year] as const,
};

export const PAGE_SIZE = 25;

export function useCropHistoryGrid(
  farmId: string | null,
  filters: CropHistoryFilters,
): UseQueryResult<CropHistoryGrid, Error> {
  return useQuery<CropHistoryGrid, Error>({
    queryKey: cropHistoryKeys.grid(farmId, filters),
    enabled: Boolean(farmId),
    queryFn: () => {
      const p = new URLSearchParams({ limit: String(PAGE_SIZE), offset: String(filters.offset) });
      // Флаг уезжает в адрес ЧИСЛОМ: строка даёт `%221%22` и сервер её не читает
      // (грабля TanStack-search репозитория).
      if (filters.onlyGaps) p.set('only_gaps', '1');
      if (filters.search.trim()) p.set('search', filters.search.trim());
      return api.get<CropHistoryGrid>(`/api/v1/farms/${farmId}/crop-history?${p.toString()}`);
    },
    staleTime: 15_000,
  });
}

export function useCellLog(
  farmId: string | null,
  fieldId: string | null,
  year: number | null,
): UseQueryResult<{ items: HistoryLogEntry[] }, Error> {
  return useQuery<{ items: HistoryLogEntry[] }, Error>({
    queryKey: cropHistoryKeys.log(farmId, fieldId ?? 'none', year ?? 0),
    enabled: Boolean(farmId && fieldId && year),
    queryFn: () =>
      api.get<{ items: HistoryLogEntry[] }>(
        `/api/v1/farms/${farmId}/crop-history/log?field_id=${fieldId}&year=${year}`,
      ),
    staleTime: 0,
  });
}
